import React, { useEffect } from 'react';
import { styled } from '@mui/material';
import Navbar from '../../components/Navbar';
import theme from '../../styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTiktok } from '@fortawesome/free-brands-svg-icons';
import { faRightToBracket, faUpload, faPaperPlane, faShieldHalved, faTerminal } from '@fortawesome/free-solid-svg-icons';

const TikTok = () => {
  useEffect(() => {
    document.title = 'TikTok Integration - Digital Porters';
  }, []);

  return (
    <MainContainer>
      <Navbar
        position='sticky'
        customtextcolor={theme.color.textPrimary}
        searchBarBackgroundColor={theme.color.beige}
      />

      <Hero>
        <FontAwesomeIcon icon={faTiktok} color={theme.color.light} size='3x' />
        <HeroTitle>TikTok Integration</HeroTitle>
        <HeroSubtitle>
          How Digital Porters uses TikTok for Developers to publish the owner's
          short-form videos from a single content workflow.
        </HeroSubtitle>
      </Hero>

      <Section>
        <SectionTitle>Overview</SectionTitle>
        <Paragraph>
          Digital Porters is a personal content automation tool used by the app
          owner to publish their own short-form videos to multiple platforms
          from a single workflow. The TikTok integration runs only against the
          app owner's authenticated account and is not exposed to third-party
          users.
        </Paragraph>
      </Section>

      <Section dark>
        <SectionTitle light>Products & Scopes</SectionTitle>

        <CardsContainer>
          <Card>
            <CardIcon><FontAwesomeIcon icon={faRightToBracket} color={theme.color.primary} size='2x' /></CardIcon>
            <CardTitle>Login Kit</CardTitle>
            <CardBody>
              The app owner signs in with their own TikTok account via the
              standard OAuth 2.0 flow on a desktop browser. After consenting,
              they are redirected back to the local app, which exchanges the
              authorization code for an access token. No other users
              authenticate.
            </CardBody>
          </Card>

          <Card>
            <CardIcon><FontAwesomeIcon icon={faUpload} color={theme.color.primary} size='2x' /></CardIcon>
            <CardTitle>video.upload</CardTitle>
            <CardSubtitle>Content Posting API</CardSubtitle>
            <CardBody>
              Uploads the local video file to TikTok's servers in a single
              chunk via the init / upload / status endpoints. The upload
              progresses through TikTok's processing pipeline until the
              status returns FINISHED.
            </CardBody>
          </Card>

          <Card>
            <CardIcon><FontAwesomeIcon icon={faPaperPlane} color={theme.color.primary} size='2x' /></CardIcon>
            <CardTitle>video.publish</CardTitle>
            <CardSubtitle>Content Posting API · Direct Post</CardSubtitle>
            <CardBody>
              Publishes the uploaded video directly to the authenticated
              user's own TikTok feed with a caption, hashtags, and standard
              privacy and interaction settings (public, with comments, duet,
              and stitch enabled).
            </CardBody>
          </Card>
        </CardsContainer>
      </Section>

      <Section>
        <SectionTitle>End-to-end flow</SectionTitle>

        <StepsContainer>
          <Step>
            <StepNumber>1</StepNumber>
            <StepBody>
              <StepTitle>Owner runs the CLI</StepTitle>
              <StepText>
                The app owner runs <Code>uploader auth tiktok</Code> from a
                local terminal.
              </StepText>
            </StepBody>
          </Step>
          <Step>
            <StepNumber>2</StepNumber>
            <StepBody>
              <StepTitle>OAuth consent (Login Kit)</StepTitle>
              <StepText>
                A browser opens to TikTok's consent screen, which lists the
                requested scopes (<Code>video.upload</Code>, <Code>video.publish</Code>).
                The owner approves with their own TikTok account.
              </StepText>
            </StepBody>
          </Step>
          <Step>
            <StepNumber>3</StepNumber>
            <StepBody>
              <StepTitle>Token exchange</StepTitle>
              <StepText>
                The CLI exchanges the authorization code for an access token
                and refresh token, stored locally on the owner's machine.
              </StepText>
            </StepBody>
          </Step>
          <Step>
            <StepNumber>4</StepNumber>
            <StepBody>
              <StepTitle>Upload (video.upload)</StepTitle>
              <StepText>
                The owner runs <Code>uploader upload video.mp4 -p tiktok</Code>.
                The CLI initializes a Direct Post upload, transfers the video
                file in a single chunk, and polls until processing is done.
              </StepText>
            </StepBody>
          </Step>
          <Step>
            <StepNumber>5</StepNumber>
            <StepBody>
              <StepTitle>Publish (video.publish)</StepTitle>
              <StepText>
                Once the status returns PUBLISH_COMPLETE, the video appears
                on the owner's own TikTok feed with the caption and settings
                provided by the CLI.
              </StepText>
            </StepBody>
          </Step>
        </StepsContainer>
      </Section>

      <Section dark>
        <SectionTitle light>Scope of access</SectionTitle>
        <CardsContainer>
          <InfoCard>
            <InfoIcon><FontAwesomeIcon icon={faShieldHalved} color={theme.color.accent} size='2x' /></InfoIcon>
            <InfoTitle>Single account</InfoTitle>
            <InfoBody>
              Only the app owner authenticates. There is no public sign-up,
              no third-party authentication path, and no access to other
              users' accounts.
            </InfoBody>
          </InfoCard>
          <InfoCard>
            <InfoIcon><FontAwesomeIcon icon={faTerminal} color={theme.color.accent} size='2x' /></InfoIcon>
            <InfoTitle>Local CLI only</InfoTitle>
            <InfoBody>
              The integration runs from a local command-line tool on the
              owner's machine. Tokens are stored locally and never shared
              with third parties.
            </InfoBody>
          </InfoCard>
        </CardsContainer>
      </Section>

      <Footer>
        <FooterText>
          Questions about this integration? Email{' '}
          <FooterLink href='mailto:accounts@digitalporters.com'>
            accounts@digitalporters.com
          </FooterLink>.
        </FooterText>
      </Footer>
    </MainContainer>
  );
};

const MainContainer = styled('div')(() => ({
  backgroundColor: theme.color.cream,
  minHeight: '100vh',
}));

const Hero = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.color.primary,
  padding: '80px 30px',
  gap: 20,
  textAlign: 'center',
}));
const HeroTitle = styled('h1')(() => ({
  color: theme.color.light,
  fontFamily: theme.fontFamily.primary,
  fontSize: 48,
  margin: 0,
}));
const HeroSubtitle = styled('p')(() => ({
  color: theme.color.light,
  fontFamily: theme.fontFamily.tertiary,
  fontSize: 18,
  maxWidth: 720,
  lineHeight: 1.6,
  margin: 0,
  opacity: 0.9,
}));

const Section = styled('div')(({ dark }) => ({
  padding: '60px 30px',
  backgroundColor: dark ? theme.color.beige : theme.color.cream,
}));
const SectionTitle = styled('h2')(({ light }) => ({
  color: light ? theme.color.primary : theme.color.primary,
  fontFamily: theme.fontFamily.primary,
  fontSize: 32,
  textAlign: 'center',
  margin: '0 0 40px 0',
}));
const Paragraph = styled('p')(() => ({
  color: theme.color.textPrimary,
  fontFamily: theme.fontFamily.tertiary,
  fontSize: 17,
  lineHeight: 1.7,
  maxWidth: 760,
  margin: '0 auto',
  textAlign: 'center',
}));

const CardsContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'stretch',
  gap: 24,
  maxWidth: 1100,
  margin: '0 auto',
}));
const Card = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 12,
  backgroundColor: theme.color.light,
  padding: 28,
  borderRadius: theme.borderRadius.medium,
  flex: '1 1 280px',
  maxWidth: 340,
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
}));
const CardIcon = styled('div')(() => ({
  marginBottom: 4,
}));
const CardTitle = styled('h3')(() => ({
  color: theme.color.primary,
  fontFamily: theme.fontFamily.primary,
  fontSize: 22,
  margin: 0,
}));
const CardSubtitle = styled('div')(() => ({
  color: theme.color.brick,
  fontFamily: theme.fontFamily.tertiary,
  fontSize: 13,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
}));
const CardBody = styled('p')(() => ({
  color: theme.color.textPrimary,
  fontFamily: theme.fontFamily.tertiary,
  fontSize: 15,
  lineHeight: 1.6,
  margin: 0,
}));

const StepsContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  maxWidth: 760,
  margin: '0 auto',
}));
const Step = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 20,
  backgroundColor: theme.color.light,
  padding: 24,
  borderRadius: theme.borderRadius.medium,
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
}));
const StepNumber = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: theme.color.primary,
  color: theme.color.light,
  fontFamily: theme.fontFamily.primary,
  fontSize: 20,
  flexShrink: 0,
}));
const StepBody = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
}));
const StepTitle = styled('h4')(() => ({
  color: theme.color.primary,
  fontFamily: theme.fontFamily.primary,
  fontSize: 18,
  margin: 0,
}));
const StepText = styled('p')(() => ({
  color: theme.color.textPrimary,
  fontFamily: theme.fontFamily.tertiary,
  fontSize: 15,
  lineHeight: 1.6,
  margin: 0,
}));
const Code = styled('code')(() => ({
  backgroundColor: theme.color.beige,
  color: theme.color.brown,
  fontFamily: 'source-code-pro, Menlo, Monaco, Consolas, monospace',
  fontSize: 13,
  padding: '2px 6px',
  borderRadius: 4,
}));

const InfoCard = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 12,
  backgroundColor: theme.color.primary,
  padding: 28,
  borderRadius: theme.borderRadius.medium,
  flex: '1 1 280px',
  maxWidth: 380,
}));
const InfoIcon = styled('div')(() => ({
  marginBottom: 4,
}));
const InfoTitle = styled('h3')(() => ({
  color: theme.color.light,
  fontFamily: theme.fontFamily.primary,
  fontSize: 22,
  margin: 0,
}));
const InfoBody = styled('p')(() => ({
  color: theme.color.light,
  fontFamily: theme.fontFamily.tertiary,
  fontSize: 15,
  lineHeight: 1.6,
  margin: 0,
  opacity: 0.9,
}));

const Footer = styled('div')(() => ({
  padding: '40px 30px',
  textAlign: 'center',
  backgroundColor: theme.color.cream,
}));
const FooterText = styled('p')(() => ({
  color: theme.color.textSecondary,
  fontFamily: theme.fontFamily.tertiary,
  fontSize: 14,
  margin: 0,
}));
const FooterLink = styled('a')(() => ({
  color: theme.color.brick,
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

export default TikTok;
